import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  ChatLink,
  ChatPair,
  MapMember,
  MemberMapResponse,
  SpaceIndexRow,
  SpaceWindow,
} from '../data-transfer-objects/member-map.dto';

export interface MemberMapQuery {
  field: 'lastSeen' | 'createdAt';
  /** 1h, 1d, 7d... or empty for all time. */
  timeframe: string;
  /** Omit for no spaces layer. */
  spaces?: SpaceWindow | null;
  /** Members of this one space instead of the timeframe. */
  space?: string | null;
}

/** The members map's two reads. Kept apart from UsersService's paged list. */
@Injectable({ providedIn: 'root' })
export class MemberMapService {
  constructor(private http: HttpClient) {}

  getMap(q: MemberMapQuery): Observable<MemberMapResponse> {
    let params = new HttpParams()
      .set('field', q.field)
      .set('timeframe', q.timeframe);
    if (q.spaces) params = params.set('spaces', q.spaces);
    if (q.space) params = params.set('space', q.space);
    return this.http.get<MemberMapResponse>(`${environment.baseURL}/users/map`, {
      params,
    });
  }

  getChatLinks(): Observable<{ links: ChatLink[] }> {
    return this.http.get<{ links: ChatLink[] }>(
      `${environment.baseURL}/users/map/chat-links`
    );
  }

  /** How far each chat between these pairs got. At most 25 pairs. */
  getChatPairs(pairs: Array<[string, string]>): Observable<{ pairs: ChatPair[] }> {
    const params = new HttpParams().set(
      'pairs',
      pairs.map(([a, b]) => `${a}:${b}`).join(',')
    );
    return this.http.get<{ pairs: ChatPair[] }>(
      `${environment.baseURL}/users/map/chat-pairs`,
      { params }
    );
  }

  /** Located members by name, company, role or city, most recently seen first. */
  searchMembers(q: string): Observable<{ members: MapMember[] }> {
    return this.http.get<{ members: MapMember[] }>(
      `${environment.baseURL}/users/map/search`,
      { params: new HttpParams().set('q', q) }
    );
  }

  /** Every space with a member, newest created first. */
  getSpaceIndex(): Observable<{ spaces: SpaceIndexRow[] }> {
    return this.http.get<{ spaces: SpaceIndexRow[] }>(
      `${environment.baseURL}/users/map/spaces`
    );
  }
}
